import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { cyan, green, purple } from '@mui/material/colors';
import noop from 'lodash/noop';
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as DeleteIcon } from '../../../assets/images/icon-delete-trashbin.svg';
import iconDigitalItemColored from '../../../assets/images/icon-digital-item-colored.svg';
import { ReactComponent as EditIcon } from '../../../assets/images/icon-edit-pencil.svg';
import iconImagePlaceholder from '../../../assets/images/icon-image-placeholder.svg';
import iconMskuItemColored from '../../../assets/images/icon-msku-item-colored.svg';
import iconPhysicalItemColored from '../../../assets/images/icon-physical-item-colored.svg';

import {
  DIGITAL,
  GenderLabels,
  INVENTORY_COLUMN_IDS,
  MSKU,
  PHYSICAL,
  VISIBLE_INVENTORY_COLS_INITIAL_STATE,
} from '../../../constants/inventories';
import { INITIAL_PAGE } from '../../../constants/table';
import { useCurrency } from '../../../contexts/CurrencyProvider';
import useClearFiltersCallback from '../../../hooks/useClearFiltersCallback';
import useColumnsVisibility from '../../../hooks/useColumnsVisibility';
import useCopyClipboard from '../../../hooks/useCopyClipboard';
import usePopover from '../../../hooks/usePopover';
import { changeInventoryColumnsVisibility } from '../../../store/actions/tools';
import { selectFulfillmentCenterItems } from '../../../store/selectors/fulfillmentCenters';
import { selectCurrentOrganizationDepartmentList } from '../../../store/selectors/organizations';
import { selectInventoryVisibleColumns } from '../../../store/selectors/tools';
import { IInventoryItem } from '../../../types/inventories';
import { IFiltersRef } from '../../../types/tables';
import { getCountryNameByTwoDigits } from '../../../utils/country';
import { getHighlightStyles } from '../../../utils/highlightStyles';
import {
  getDescriptionDisplayText,
  getItemCountWarning,
  isItemDigital,
  isItemMsku,
  isItemOutOfStock,
  isPreorderItem,
} from '../../../utils/inventories';
import { defaultTableStyles, getDefaultStickyStyles } from '../../../utils/tables';
import { Price } from '../../index';
import { IPaginatedListChangeEvent } from '../../PaginatedList/PaginatedList';
import ColumnsVisibilityPopover from '../../UsersComponent/components/ColumnsVisibilityPopover';
import { CopyableLink, PageContent, TableFilteredEmptyPlaceholder, TableMUI } from '../../_mui';
import { InventoryContextMenu } from '../components/InventoryContextMenu';
import { useExpandableRows } from '../hooks/useExpandableRows';
import { useInventoryContextMenu } from '../hooks/useInventoryContextMenu';

interface IProps {
  items: IInventoryItem[];
  searchQuery: string;
  totalCount: number;
  onChange: ({ page, setPage }: IPaginatedListChangeEvent) => void;
  isLoading: boolean;
  canEditInventory: boolean;
  onDelete?: (itemId: string) => void;
  onEdit?: (item: IInventoryItem) => void;
  page: number;
  pageSize: number;
}

const getItemTypeBadgeStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: '50%',
  flexShrink: 0,
  border: `1px solid ${theme.palette.common.white}`,
});

const InventoryListView = forwardRef<IFiltersRef, IProps>(
  (
    { items, searchQuery, totalCount, onChange, isLoading, canEditInventory, onDelete, onEdit, page, pageSize }: IProps,
    filtersRef,
  ) => {
    const theme = useTheme();
    const { sass, palette } = theme;
    const dispatch = useDispatch();
    const { getItemPrice } = useCurrency();
    const { setCopied } = useCopyClipboard({ successDuration: 1000, toastDuration: 1000 });
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

    const { expandedRows, toggleRow: handleToggleRow } = useExpandableRows();

    const initialVisibleColumns = useSelector(selectInventoryVisibleColumns);
    const fulfillmentCenters = useSelector(selectFulfillmentCenterItems);
    const departments = useSelector(selectCurrentOrganizationDepartmentList);
    const itemTypeBadge = getItemTypeBadgeStyles(theme);

    const handlePageChange = useCallback(
      (newPage: number) => {
        if (onChange) {
          onChange({
            page: newPage,
            pageSize,
            setPage: noop,
            setPageSize: noop,
          });
        }
      },
      [onChange, pageSize],
    );

    const handlePageSizeChange = useCallback(
      (newPageSize: number) => {
        if (onChange) {
          onChange({
            page: INITIAL_PAGE,
            pageSize: newPageSize,
            setPage: noop,
            setPageSize: noop,
          });
        }
      },
      [onChange],
    );

    const {
      anchorEl: visibleColsAnchorEl,
      position: popoverPosition,
      isOpen: isVisibleColsPopoverOpen,
      handleOpen: handleVisibleColsMenuOpen,
      handleClose: handleVisibleColsMenuClose,
    } = usePopover();

    const {
      visibleColumns,
      columnSearchQuery,
      setColumnSearchQuery,
      handleColumnVisibilityChange,
      handleToggleAllColumns,
      handleResetColumns,
      areAllColumnsVisible,
    } = useColumnsVisibility({
      initialState: initialVisibleColumns,
      requiredColumnIds: [INVENTORY_COLUMN_IDS.PRODUCT_NAME, INVENTORY_COLUMN_IDS.ACTIONS],
      onChange: (newState: Record<string, boolean>) => dispatch(changeInventoryColumnsVisibility(newState)),
      defaultColumns: VISIBLE_INVENTORY_COLS_INITIAL_STATE,
    });

    const {
      menuAnchorEl,
      handleMenuOpen,
      handleMenuClose,
      selectedItem,
      handleRowClick,
      handleEditItem,
      handleDeleteItem,
    } = useInventoryContextMenu({
      onDelete,
      onEdit,
    });

    const defaultColumnEvents = useMemo(
      () => ({
        onBodyCellDoubleClick: handleRowClick,
        onCellContextMenu: (e: React.MouseEvent<HTMLElement, MouseEvent>, item: IInventoryItem, columnId?: string) => {
          if (columnId !== INVENTORY_COLUMN_IDS.ACTIONS) {
            e.preventDefault();
            setMenuPosition({
              top: e.pageY,
              left: e.pageX,
            });
            handleMenuOpen(e, item);
          }
        },
      }),
      [handleMenuOpen, handleRowClick],
    );

    const handleLocalMenuClose = useCallback(() => {
      setMenuPosition(null);
      handleMenuClose();
    }, [handleMenuClose]);

    const handleItemToggle = useCallback(
      (item: IInventoryItem) => {
        handleToggleRow(item.item_id);
      },
      [handleToggleRow],
    );

    const getItemTypeBadge = useCallback(
      (item: IInventoryItem) => {
        switch (item.type) {
          case DIGITAL:
            return {
              icon: iconDigitalItemColored,
              backgroundColor: cyan[50],
              tooltip: 'Digital item',
              size: 16,
            };

          case MSKU:
            return {
              icon: iconMskuItemColored,
              backgroundColor: purple[50],
              tooltip: 'MSKU item',
              size: 16,
            };

          case PHYSICAL:
          default:
            return {
              icon: iconPhysicalItemColored,
              backgroundColor: sass.physicalItemBadgeBgColor,
              tooltip: 'Physical item',
              size: 20,
            };
        }
      },
      [sass.physicalItemBadgeBgColor],
    );

    const iconSize = useMemo(() => ({ width: 16, height: 16 }), []);

    const getBackorderStatusIcon = useCallback(
      (item: IInventoryItem) => {
        switch (true) {
          case isItemDigital(item):
            return <Typography>N/A</Typography>;

          case isPreorderItem(item):
            return <CheckIcon sx={{ ...iconSize, color: green[800] }} />;

          default:
            return <CloseIcon sx={{ ...iconSize, color: sass.errorRed }} />;
        }
      },
      [iconSize, sass.errorRed],
    );

    const getStockDisplayData = useCallback(
      (item: IInventoryItem) => {
        if (isItemDigital(item)) {
          return { text: 'N/A', icon: null, showCount: false, tooltip: null };
        }

        const warningMessage = getItemCountWarning(item);
        const isAvailable = !isItemOutOfStock(item);
        const icon = warningMessage ? (
          isAvailable ? (
            <WarningAmberIcon color="warning" sx={{ ...iconSize }} />
          ) : (
            <ErrorOutlineIcon color="error" sx={{ ...iconSize }} />
          )
        ) : null;
        const isMsku = isItemMsku(item);
        const count = item.count ?? 0;
        const shouldShowCount = !isMsku && count !== -1;

        return {
          text: shouldShowCount ? count.toString() : null,
          icon,
          showCount: shouldShowCount,
          tooltip: warningMessage,
        };
      },
      [iconSize],
    );

    const renderStockCell = useCallback(
      (item: IInventoryItem) => {
        const data = getStockDisplayData(item);

        if (data.text === 'N/A') {
          return <Typography>{data.text}</Typography>;
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {data.showCount && <Typography>{data.text}</Typography>}
            {data.icon && (
              <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {data.tooltip ? (
                  <Tooltip title={data.tooltip} arrow placement="top">
                    {data.icon}
                  </Tooltip>
                ) : (
                  data.icon
                )}
              </Box>
            )}
          </Box>
        );
      },
      [getStockDisplayData],
    );

    const iconStyles = useMemo(
      () => ({
        width: 15,
        height: 15,
      }),
      [],
    );

    const renderActionControls = useCallback(
      (item: IInventoryItem, isChildRow?: boolean) => {
        if (isChildRow || !canEditInventory) {
          return null;
        }

        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', justifyContent: 'space-around' }}>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(item);
              }}
              data-action="list_view_edit_btn"
            >
              <Tooltip title="Edit item" arrow placement="top">
                <EditIcon style={{ ...iconStyles }} />
              </Tooltip>
            </Box>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(item.item_id);
              }}
              data-action="list_view_delete_btn"
            >
              <Tooltip title="Delete item" arrow placement="top">
                <DeleteIcon style={{ ...iconStyles }} />
              </Tooltip>
            </Box>
          </Box>
        );
      },
      [onEdit, onDelete, canEditInventory, iconStyles],
    );

    const allColumns = useMemo(() => {
      const { stickyLeftCellStyle, actionsCellStyle } = getDefaultStickyStyles(theme, 85);
      const { bodyCellStyle, minWidthBodyCellStyle, threeLinesEllipsisContainer, doubleLineEllipsisContainer } =
        defaultTableStyles(theme);
      const { mark } = getHighlightStyles(theme);

      return [
        {
          id: INVENTORY_COLUMN_IDS.PRODUCT_NAME,
          label: 'Product',
          ...defaultColumnEvents,
          minWidth: 360,
          headerCellStyle: {
            ...stickyLeftCellStyle,
            zIndex: { xs: '2 !important', sm: '11 !important' },
            paddingLeft: 2.5,
          },
          bodyCellStyle: (item: IInventoryItem, isChildRow?: boolean) => {
            const isMsku = isItemMsku(item);
            const hasSkuOptions = isMsku && item.sku_options && item.sku_options.length > 0;
            const isExpanded = expandedRows.has(item.item_id);
            const shouldShowColoredBorder = (hasSkuOptions && isExpanded) || isChildRow;

            return {
              ...bodyCellStyle,
              ...stickyLeftCellStyle,
              minWidth: 360,
              borderLeft: shouldShowColoredBorder ? `2px solid ${palette.primary.main}` : '2px solid transparent',
            };
          },
          render: (item: IInventoryItem, isChildRow?: boolean) => {
            const badge = getItemTypeBadge(item);
            const isMsku = isItemMsku(item);
            const hasSkuOptions = isMsku && item.sku_options && item.sku_options.length > 0;
            const isExpanded = expandedRows.has(item.item_id);

            const highlightedName = (
              <Highlighter
                highlightStyle={mark}
                searchWords={[searchQuery]}
                autoEscape
                textToHighlight={item.name || ''}
              />
            );

            return (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {!isChildRow && hasSkuOptions ? (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleItemToggle(item);
                      }}
                      sx={{ padding: 0.5, flexShrink: 0 }}
                    >
                      {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                    </IconButton>
                  ) : (
                    <Box sx={{ width: isChildRow ? 0 : 32, flexShrink: 0 }} />
                  )}
                  {item.image_url ? (
                    <Box
                      component="img"
                      src={item.image_url}
                      alt={item.name}
                      sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                    />
                  ) : (
                    iconImagePlaceholder
                  )}
                  <Box sx={{ flexGrow: 1, minWidth: isChildRow ? 246 : 270, maxWidth: isChildRow ? 246 : 270 }}>
                    <CopyableLink
                      value={item.name}
                      onTextClick={() => handleRowClick(item)}
                      textTooltip={item.name}
                      copyTooltip="Copy name"
                      textStyle={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                      }}
                      toastLabel="Product name"
                    >
                      {highlightedName}
                    </CopyableLink>
                  </Box>
                  <Tooltip title={badge.tooltip} arrow placement="top">
                    <Box sx={{ ...itemTypeBadge, backgroundColor: badge.backgroundColor, flexShrink: 0 }}>
                      <Box
                        component="img"
                        src={badge.icon}
                        alt={badge.tooltip}
                        sx={{ width: badge.size, height: badge.size }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            );
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.SKU,
          label: 'SKU',
          ...defaultColumnEvents,
          bodyCellStyle: {
            ...bodyCellStyle,
            minWidth: 300,
            maxWidth: 300,
            textAlign: 'left',
          },
          render: (item: IInventoryItem) => {
            const sku = item.product_sku;

            return sku ? (
              <CopyableLink
                value={sku || ''}
                onTextClick={() => setCopied(sku || '', 'SKU')}
                textTooltip={sku}
                copyTooltip="Copy SKU"
                toastLabel="SKU"
                textStyle={{
                  color: sass?.ebony,
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                <Highlighter highlightStyle={mark} searchWords={[searchQuery]} autoEscape textToHighlight={sku || ''} />
              </CopyableLink>
            ) : (
              <Typography sx={{ textAlign: 'left' }}>N/A</Typography>
            );
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.STOCK,
          label: 'Stock',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: renderStockCell,
        },
        {
          id: INVENTORY_COLUMN_IDS.PRICE,
          label: 'Price',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (item: IInventoryItem) => {
            const { currency_code, sku_options, type, price: itemPrice } = item;
            const displayedPrice = getItemPrice({ price: itemPrice, type, sku_options, currency_code });
            return <Price value={displayedPrice} />;
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.WAREHOUSE,
          label: 'Warehouse',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (item: IInventoryItem) => {
            const fulfillmentCenter = fulfillmentCenters?.find((fc) => fc.uid === item.fulfillment_center_id);
            const noFC = fulfillmentCenter?.uid === 'no_fc';
            return <Typography>{noFC ? 'N/A' : fulfillmentCenter?.name || 'N/A'}</Typography>;
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.DEPARTMENTS,
          label: 'Departments',
          ...defaultColumnEvents,
          bodyCellStyle,
          render: (item: IInventoryItem) => {
            const departmentNames = Array.isArray(item.department_ids)
              ? item.department_ids
                  .map((deptId) => departments?.find((dept) => dept.uid === deptId)?.name)
                  .filter(Boolean)
                  .join(', ')
              : undefined;
            return <Typography sx={{ ...doubleLineEllipsisContainer }}>{departmentNames || 'N/A'}</Typography>;
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.BACKORDER_STATUS,
          label: 'Backorder status',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (item: IInventoryItem) => {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                {getBackorderStatusIcon(item)}
              </Box>
            );
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.DESCRIPTION,
          label: 'Item description',
          ...defaultColumnEvents,
          bodyCellStyle,
          render: (item: IInventoryItem) => {
            const description = getDescriptionDisplayText(item.description);

            return (
              <Tooltip
                title={description}
                arrow
                placement="top"
                slotProps={{ tooltip: { sx: { whiteSpace: 'pre-wrap' } } }}
              >
                <Typography component="span" sx={{ ...threeLinesEllipsisContainer, whiteSpace: 'pre-wrap' }}>
                  {description}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.COUNTRY_OF_ORIGIN,
          label: 'Country of origin',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (item: IInventoryItem) => {
            const digitalItem = isItemDigital(item);
            if (digitalItem) {
              return <Typography>N/A</Typography>;
            }
            const countryName = item.country_of_origin ? getCountryNameByTwoDigits(item.country_of_origin)?.name : '';
            return (
              <Tooltip title={countryName} arrow placement="top">
                <Typography component="span" sx={{ ...doubleLineEllipsisContainer }}>
                  {countryName}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.FLAVOR,
          label: 'Flavor',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (item: IInventoryItem) => {
            const digitalItem = isItemDigital(item);
            return <Typography>{digitalItem ? 'N/A' : item.flavor || ''}</Typography>;
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.GENDER,
          label: 'Style',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (item: IInventoryItem) => {
            const digitalItem = isItemDigital(item);
            return <Typography>{digitalItem ? 'N/A' : item.gender ? GenderLabels[item.gender] : ''}</Typography>;
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.COLOR,
          label: 'Color',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (item: IInventoryItem) => {
            const digitalItem = isItemDigital(item);
            return <Typography>{digitalItem ? 'N/A' : item.color || ''}</Typography>;
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.SIZE,
          label: 'Size',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (item: IInventoryItem) => {
            const digitalItem = isItemDigital(item);
            return <Typography>{digitalItem ? 'N/A' : item.size || ''}</Typography>;
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.LOW_STOCK_THRESHOLD,
          label: 'Low stock threshold',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (item: IInventoryItem) => {
            const digitalItem = isItemDigital(item);
            return <Typography>{digitalItem ? 'N/A' : item.low_stock_threshold || '0'}</Typography>;
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.HARMONIZATION_CODE,
          label: 'Harmonization code',
          ...defaultColumnEvents,
          bodyCellStyle: minWidthBodyCellStyle,
          render: (item: IInventoryItem) => {
            const digitalItem = isItemDigital(item);
            return <Typography>{digitalItem ? 'N/A' : item.hts_code || ''}</Typography>;
          },
        },
        {
          id: INVENTORY_COLUMN_IDS.ACTIONS,
          label: (
            <Tooltip title="Show/hide columns" arrow placement="top">
              <TuneIcon color="action" data-action="columns_visibility_toggle" />
            </Tooltip>
          ),
          headerCellStyle: actionsCellStyle,
          bodyCellStyle: actionsCellStyle,
          onHeaderCellClick: (e: React.MouseEvent<HTMLElement>) => {
            handleVisibleColsMenuOpen(e);
          },
          render: renderActionControls,
        },
      ];
    }, [
      defaultColumnEvents,
      departments,
      expandedRows,
      fulfillmentCenters,
      getBackorderStatusIcon,
      getItemPrice,
      getItemTypeBadge,
      handleItemToggle,
      handleVisibleColsMenuOpen,
      renderStockCell,
      searchQuery,
      theme,
      handleRowClick,
      itemTypeBadge,
      palette,
      renderActionControls,
      setCopied,
      sass.ebony,
    ]);

    const columns = useMemo(() => {
      return allColumns.filter((column) => visibleColumns[column.id]);
    }, [allColumns, visibleColumns]);

    const handleClearFilters = useClearFiltersCallback(
      filtersRef && typeof filtersRef !== 'function' ? filtersRef : null,
    );

    const placeholder = useMemo(
      () => <TableFilteredEmptyPlaceholder itemType="inventory items" onClearFilters={handleClearFilters} />,
      [handleClearFilters],
    );

    return (
      <PageContent>
        <TableMUI
          maxHeight="unset"
          containerStyle={{ width: '100%' }}
          data={items}
          columns={columns}
          page={page}
          rowsPerPage={pageSize}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
          isLoading={isLoading}
          emptyPlaceholderComponent={placeholder}
          activeRow={selectedItem}
          menuAnchorEl={menuAnchorEl}
          handleMenuClose={handleLocalMenuClose}
          getChildRows={(item) => item.sku_options}
          isRowExpandable={(item) => isItemMsku(item) && !!item.sku_options && item.sku_options.length > 0}
          expandedRows={expandedRows}
          onToggleRow={handleItemToggle}
          getRowId={(item) => item.item_id}
        />
        <InventoryContextMenu
          anchorEl={menuAnchorEl}
          menuPosition={menuPosition}
          selectedItem={selectedItem}
          canEditInventory={canEditInventory}
          onClose={handleLocalMenuClose}
          onEditItem={handleEditItem}
          onDeleteItem={handleDeleteItem}
          onViewItem={handleRowClick}
        />
        <ColumnsVisibilityPopover
          anchorEl={visibleColsAnchorEl}
          position={popoverPosition}
          open={isVisibleColsPopoverOpen}
          onClose={handleVisibleColsMenuClose}
          columns={allColumns}
          visibleColumns={visibleColumns}
          searchQuery={columnSearchQuery}
          onSearchChange={setColumnSearchQuery}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          onToggleAll={handleToggleAllColumns}
          onReset={handleResetColumns}
          requiredColumnIds={[INVENTORY_COLUMN_IDS.PRODUCT_NAME, INVENTORY_COLUMN_IDS.ACTIONS]}
          areAllColumnsVisible={areAllColumnsVisible}
        />
      </PageContent>
    );
  },
);

export default InventoryListView;
